import React from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Search, ArrowUpDown, ArrowUp, ArrowDown, CalendarRange, Filter, RotateCcw, Building, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const BookingFilters = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  corporateFilter,
  setCorporateFilter,
  corporateClients,
  sortColumn,
  setSortColumn,
  sortDirection,
  setSortDirection,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  onApplyFilters,
  onClearFilters,
  filteredBookingsCount
}) => {
  const navigate = useNavigate();
  const selectedCorporate = corporateClients?.find(c => c.id === corporateFilter);

  return (
    <div className="flex flex-col gap-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search bookings..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-secondary/50 border-white/10"
          data-testid="search-input"
        />
      </div>
      
      {/* Status, Corporate & Sort - Row on mobile */}
      <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-secondary/50 border-white/10 text-sm" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="dispatched">Dispatched</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Corporate Client Filter */}
        <Select value={corporateFilter} onValueChange={setCorporateFilter}>
          <SelectTrigger className="bg-secondary/50 border-white/10 text-sm" data-testid="corporate-filter">
            <Building className="h-4 w-4 mr-1" />
            <SelectValue placeholder="Client" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {corporateClients?.map(client => (
              <SelectItem key={client.id} value={client.id}>
                {client.company_name || client.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={sortColumn} onValueChange={setSortColumn}>
          <SelectTrigger className="bg-secondary/50 border-white/10 text-sm" data-testid="sort-column">
            <ArrowUpDown className="h-4 w-4 mr-1 sm:mr-2" />
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date">Date/Time</SelectItem>
            <SelectItem value="passenger">Passenger</SelectItem>
            <SelectItem value="status">Status</SelectItem>
            <SelectItem value="vehicle">Vehicle</SelectItem>
            <SelectItem value="reference">Reference</SelectItem>
          </SelectContent>
        </Select>
        
        <Button
          variant="outline"
          onClick={() => setSortDirection(prev => prev === "asc" ? "desc" : "asc")}
          className="bg-secondary/50 border-white/10 col-span-2 sm:col-span-1 sm:w-10"
          data-testid="sort-direction-btn"
          title={sortDirection === "asc" ? "Ascending" : "Descending"}
        >
          {sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
          <span className="ml-2 sm:hidden">{sortDirection === "asc" ? "Oldest First" : "Newest First"}</span>
        </Button>
      </div>
      
      {/* Corporate Invoice Button - Show when filtering by corporate */}
      {corporateFilter && corporateFilter !== "all" && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20">
          <Building className="h-4 w-4 text-primary" />
          <span className="text-sm flex-1">
            Showing {filteredBookingsCount} booking(s) for {selectedCorporate?.company_name}
          </span>
          <Button
            size="sm"
            onClick={() => navigate(`/invoices?client=${corporateFilter}`)}
            data-testid="generate-corporate-invoice-btn"
          >
            <FileText className="h-4 w-4 mr-1" />
            Generate Invoice
          </Button>
        </div>
      )}
      
      {/* Date Range Filter - Collapsible on mobile */}
      <details className="group">
        <summary className="flex items-center gap-2 cursor-pointer p-3 rounded-lg bg-secondary/30 border border-white/5 list-none">
          <CalendarRange className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium flex-1">Date Range Filter</span>
          <span className="text-xs text-muted-foreground group-open:hidden">
            {dateFrom || dateTo ? `${dateFrom || '...'} → ${dateTo || '...'}` : 'All dates'}
          </span>
          <Filter className="h-4 w-4 text-muted-foreground" />
        </summary>
        <div className="mt-3 p-4 rounded-lg bg-secondary/30 border border-white/5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-secondary/50 border-white/10"
                data-testid="date-from"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-secondary/50 border-white/10"
                data-testid="date-to"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={onApplyFilters} className="flex-1" data-testid="apply-filter-btn">
              <Filter className="h-4 w-4 mr-2" />
              Apply
            </Button>
            <Button variant="outline" onClick={onClearFilters} className="flex-1" data-testid="clear-filter-btn">
              <RotateCcw className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>
        </div>
      </details>
    </div>
  );
};

export default BookingFilters;
